import React, { useEffect, useMemo, useRef, useState } from "react";
import ModalBase from "@components/common/Modal";

interface LawArticleDetail {
  id: string;
  title: string;
  content: string; // HTML
  lawCode?: string;
}

interface LawDetailModalProps {
  open: boolean;
  onClose: () => void;
  article?: LawArticleDetail | null;
}

const LawDetailModal: React.FC<LawDetailModalProps> = ({
  open,
  onClose,
  article,
}) => {
  const [fontSize, setFontSize] = useState<number>(16);
  const isMobile = useMemo(
    () => typeof window !== "undefined" && window.innerWidth < 768,
    []
  );

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const increase = () => setFontSize((s) => Math.min(s + 1, 28));
  const decrease = () => setFontSize((s) => Math.max(s - 1, 12));
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleDownloadPdf = async () => {
    if (!contentRef.current) return;

    const loadScript = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const exist = document.querySelector(`script[src="${src}"]`);
        if (exist) return resolve();
        const s = document.createElement("script");
        s.src = src;
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error(`Failed to load ${src}`));
        document.body.appendChild(s);
      });

    // Nạp CDN nếu chưa có
    await loadScript(
      "https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js"
    );
    await loadScript(
      "https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js"
    );

    // @ts-ignore
    const html2canvas = (window as any).html2canvas as (
      el: HTMLElement,
      opts?: any
    ) => Promise<HTMLCanvasElement>;
    // @ts-ignore
    const { jsPDF } =
      (window as any).jspdf || (window as any).window.jspdf || {};
    if (!html2canvas || !jsPDF) return;

    // Tạo canvas từ nội dung
    const canvas = await html2canvas(contentRef.current, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // Tính kích thước ảnh theo chiều rộng trang, chia nhiều trang nếu cần
    const imgWidth = pageWidth - 48; // padding ngang
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 24; // padding top

    pdf.addImage(
      imgData,
      "PNG",
      24,
      position,
      imgWidth,
      imgHeight,
      undefined,
      "FAST"
    );
    heightLeft -= pageHeight - 48; // trừ phần trang hiện tại (padding top+bottom)

    while (heightLeft > 0) {
      pdf.addPage();
      position = 24 - (imgHeight - heightLeft);
      pdf.addImage(
        imgData,
        "PNG",
        24,
        position,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      );
      heightLeft -= pageHeight - 48;
    }

    const safeTitle = (article?.title || "dieu-luat").slice(0, 80);
    pdf.save(`${safeTitle || "dieu-luat"}.pdf`);
  };

  const header = (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {isMobile && (
        <button
          onClick={onClose}
          aria-label="Back"
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#fff",
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#111"
            strokeWidth="2"
          >
            <path
              d="M15 18l-6-6 6-6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      <div
        style={{
          fontWeight: 700,
          fontSize: isMobile ? 18 : 20,
          lineHeight: 1.2,
        }}
      >
        Chi tiết điều luật
      </div>
    </div>
  );

  if (isMobile) {
    if (!open) return null;
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#fff",
          zIndex: 1000,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            height: 56,
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "0 12px",
            borderBottom: "1px solid #f0f0f0",
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={onClose}
            aria-label="Back"
            style={{
              width: 36,
              height: 36,
              borderRadius: 18,
              border: "1px solid #e5e7eb",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#111"
              strokeWidth="2"
            >
              <path
                d="M15 18l-6-6 6-6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div style={{ fontWeight: 700, fontSize: 18 }}>
            Chi tiết điều luật
          </div>
          <div style={{ width: 36 }}></div>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
          {article && (
            <div>
              <h2
                style={{
                  fontSize: fontSize + 2,
                  fontWeight: 800,
                  color: "#1f68e7",
                  marginBottom: 12,
                }}
              >
                {article.title}
              </h2>
              <div
                ref={contentRef}
                style={{ fontSize, lineHeight: 1.75, color: "#111" }}
                dangerouslySetInnerHTML={{
                  __html: article.content?.replace(/\n/g, "<br>") || "",
                }}
              />
            </div>
          )}
          {!article && (
            <div style={{ padding: 16 }}>Không có dữ liệu điều luật.</div>
          )}
        </div>

        <div
          style={{
            borderTop: "1px solid #e5e7eb",
            background: "#fff",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={decrease}
              aria-label="Giảm cỡ chữ"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontWeight: 700,
              }}
            >
              A-
            </button>
            <div style={{ minWidth: 28, textAlign: "center", fontWeight: 600 }}>
              {fontSize}
            </div>
            <button
              onClick={increase}
              aria-label="Tăng cỡ chữ"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontWeight: 700,
              }}
            >
              A+
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={handleDownloadPdf}
              aria-label="Tải PDF"
              style={{
                width: 38,
                height: 38,
                borderRadius: 19,
                border: "1px solid #e5e7eb",
                background: "#fff",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111"
                strokeWidth="2"
              >
                <path d="M12 3v12" strokeLinecap="round" />
                <path
                  d="M8 11l4 4 4-4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M4 21h16" />
              </svg>
            </button>
            {/* <button
              aria-label="Chia sẻ"
              style={{
                width: 38,
                height: 38,
                borderRadius: 19,
                border: "1px solid #e5e7eb",
                background: "#fff",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111"
                strokeWidth="2"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <path d="M8.6 13.51l6.8 3.98M15.4 6.51L8.6 10.49" />
              </svg>
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ModalBase
      open={open}
      onCancel={onClose}
      closable
      footer={null}
      title={header}
      centered
      width={820}
      bodyStyle={{ padding: 0 }}
      className={""}
    >
      <div style={{ position: "relative", height: "80vh", overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            overflow: "auto",
            padding: 16,
            paddingBottom: 82,
          }}
        >
          {article && (
            <div>
              <h2
                style={{
                  fontSize: fontSize + 2,
                  fontWeight: 800,
                  color: "#1f68e7",
                  marginBottom: 12,
                }}
              >
                {article.title}
              </h2>
              <div
                ref={contentRef}
                style={{ fontSize, lineHeight: 1.75, color: "#111" }}
                dangerouslySetInnerHTML={{
                  __html: article.content?.replace(/\n/g, "<br>") || "",
                }}
              />
            </div>
          )}
          {!article && (
            <div style={{ padding: 16 }}>Không có dữ liệu điều luật.</div>
          )}
        </div>

        {/* Footer tools */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            borderTop: "1px solid #e5e7eb",
            background: "#fff",
            padding: "10px 16px",
            paddingTop: 19,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={decrease}
              aria-label="Giảm cỡ chữ"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              A-
            </button>
            <div style={{ minWidth: 28, textAlign: "center", fontWeight: 600 }}>
              {fontSize}
            </div>
            <button
              onClick={increase}
              aria-label="Tăng cỡ chữ"
              style={{
                width: 36,
                height: 36,
                borderRadius: 18,
                border: "1px solid #e5e7eb",
                background: "#fff",
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              A+
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={handleDownloadPdf}
              aria-label="Tải PDF"
              style={{
                width: 38,
                height: 38,
                borderRadius: 19,
                border: "1px solid #e5e7eb",
                background: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111"
                strokeWidth="2"
              >
                <path d="M12 3v12" strokeLinecap="round" />
                <path
                  d="M8 11l4 4 4-4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M4 21h16" />
              </svg>
            </button>
            {/* <button
              aria-label="Chia sẻ"
              style={{
                width: 38,
                height: 38,
                borderRadius: 19,
                border: "1px solid #e5e7eb",
                background: "#fff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#111"
                strokeWidth="2"
              >
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <path d="M8.6 13.51l6.8 3.98M15.4 6.51L8.6 10.49" />
              </svg>
            </button> */}
          </div>
        </div>
      </div>
    </ModalBase>
  );
};

export default LawDetailModal;
